import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getSettings } from "./../../../services/settingsServices";
import { getMenuTree } from "./../../../services/menusServices";
import { useUser } from "../../Home/UserContext";
import { useSiteMode } from "./../../../contexts/SiteModeContext";
import { getFirstAccessibleAdminPath } from "../../../utils/adminNavigation";
import useAdminUser from "../../../hooks/useAdminUser";

import BrugesHamburger from "./BrugesHamburger";
import NavbarBrand from "./NavbarBrand";
import NavbarActions from "./NavbarActions";
import NavbarSearchPanel from "./NavbarSearchPanel";
import NavbarMegaMenu from "./NavbarMegaMenu";

import {
  getContactInfo,
  getInitials,
  getSocialLinks,
  getPrimaryBgStyle,
  getBrandBg,
  getBrandText,
  getMenuItemHref,
  isAbsolute,
} from "./navUtils";

export default function NavbarMairie() {
  const navigate = useNavigate();
  const { updateUser } = useUser();
  const { isDemo } = useSiteMode();

  const envSiteMode = process.env.REACT_APP_SITE_MODE || "mairie";
  const showAppInfo = envSiteMode === "demo" || isDemo;
  const { user: effectiveUser, permissionsLoaded } = useAdminUser();

  const [menuOpen, setMenuOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [tree, setTree] = useState([]);
  const [loadingMenu, setLoadingMenu] = useState(true);

  const [searchOpen, setSearchOpen] = useState(false);
  const [term, setTerm] = useState("");

  const searchRef = useRef(null);
  const accountRef = useRef(null);

  const [accountOpen, setAccountOpen] = useState(false);

  const normalizedUser = useMemo(() => {
    if (!effectiveUser) return null;

    return {
      ...effectiveUser,
      id: effectiveUser?.id || effectiveUser?.user_id || effectiveUser?.userId,
    };
  }, [effectiveUser]);

  const adminLandingPath = useMemo(() => {
    if (!permissionsLoaded) return null;

    const firstPath = getFirstAccessibleAdminPath(normalizedUser);

    if (firstPath) return firstPath;
    if (Number(effectiveUser?.role_id) === 1) return "/appli/dashboard";

    return null;
  }, [effectiveUser, normalizedUser, permissionsLoaded]);

  const primaryBgStyle = useMemo(() => getPrimaryBgStyle(settings), [settings]);

  const brandBg = useMemo(() => getBrandBg(), []);
  const brandText = useMemo(() => getBrandText(), []);

  const socialLinks = useMemo(() => getSocialLinks(settings), [settings]);
  const contactInfo = useMemo(() => getContactInfo(settings), [settings]);
  const initials = useMemo(() => getInitials(normalizedUser), [normalizedUser]);

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        if (mounted) setSettings(Array.isArray(data) ? data[0] : data);
      } catch (error) {
        console.error("Erreur chargement settings :", error);
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchMenu = async () => {
      try {
        const data = await getMenuTree({ menu_key: "header", locale: "fr" });
        if (mounted) setTree(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Erreur chargement menu :", error);
      } finally {
        if (mounted) setLoadingMenu(false);
      }
    };

    fetchMenu();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchOpen(false);
      }
    };

    if (searchOpen) {
      document.addEventListener("mousedown", onClickOutside);
    }

    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [searchOpen]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (accountRef.current && !accountRef.current.contains(event.target)) {
        setAccountOpen(false);
      }
    };

    if (accountOpen) {
      document.addEventListener("mousedown", onClickOutside);
    }

    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [accountOpen]);

  const handleNavigation = (path) => {
    navigate(path);
    setMenuOpen(false);
    setSearchOpen(false);
    setAccountOpen(false);
  };

  const handleAdminAccess = () => {
    setAccountOpen(false);
    setMenuOpen(false);

    if (!effectiveUser) {
      navigate("/login");
      return;
    }

    if (!permissionsLoaded) return;

    if (adminLandingPath) {
      navigate(adminLandingPath);
      return;
    }

    navigate("/");
  };

  const logout = () => {
    try {
      localStorage.removeItem("userInfo");
      localStorage.removeItem("token");
    } catch (error) {
      console.error("Erreur déconnexion :", error);
    }

    updateUser(null);
    setAccountOpen(false);
    setMenuOpen(false);
    navigate("/login");
  };

  const goToItem = (item) => {
    const href = getMenuItemHref(item);

    if (!href || href === "#") return;

    setMenuOpen(false);

    if (isAbsolute(href)) {
      window.open(
        href,
        item?.target === "_blank" ? "_blank" : "_self",
        "noopener,noreferrer",
      );
      return;
    }

    navigate(href);
  };

  const onSubmitSearch = (event) => {
    event.preventDefault();

    const q = term.trim();
    if (!q) return;

    setSearchOpen(false);
    navigate(`/recherche?q=${encodeURIComponent(q)}`);
  };

  const logoMairie = settings?.logo_mairie;
  const ville = settings?.nom_mairie || settings?.ville || "Votre mairie";
  const logoApp = "https://api.monapplimairie.com/uploads/media/images/2026/06/logo-monapplimairie_1780748151121_jgku4h.webp";

  return (
    <>
      <nav className="fixed left-0 right-0 top-0 z-40 bg-slate-950 text-white shadow-xl shadow-slate-950/20">
        <div className="mx-auto max-w-[1380px] px-0 sm:px-6">
          <div className="flex h-[80px] items-center justify-between gap-3 md:h-[100px]">
            <button
              type="button"
              onClick={() => setMenuOpen((value) => !value)}
              className="flex h-[80px] w-[80px] shrink-0 cursor-pointer select-none flex-col items-center justify-center transition hover:opacity-95 md:h-[100px] md:w-[100px]"
              aria-controls="mega-menu"
              aria-expanded={menuOpen}
              style={primaryBgStyle}
            >
              <BrugesHamburger
                open={menuOpen}
                label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
              />

              <span className="mt-2 text-[13px] font-bold tracking-wider text-white">
                {menuOpen ? "FERMER" : "MENU"}
              </span>
            </button>

            <NavbarBrand
              isDemo={showAppInfo}
              logoApp={logoApp}
              logoMairie={logoMairie}
              ville={ville}
              brandBg={brandBg}
              brandText={brandText}
              onClick={() => handleNavigation("/")}
            />

            <NavbarActions
              isDemo={showAppInfo}
              socialLinks={socialLinks}
              contactInfo={contactInfo}
              effectiveUser={effectiveUser}
              permissionsLoaded={permissionsLoaded}
              initials={initials}
              brandBg={brandBg}
              accountRef={accountRef}
              accountOpen={accountOpen}
              setAccountOpen={setAccountOpen}
              onNavigate={handleNavigation}
              onAdminAccess={handleAdminAccess}
              onLogout={logout}
              onSearchClick={() => setSearchOpen((value) => !value)}
            />
          </div>
        </div>

        {searchOpen && (
          <NavbarSearchPanel
            ref={searchRef}
            term={term}
            setTerm={setTerm}
            onSubmit={onSubmitSearch}
            brandBg={brandBg}
          />
        )}
      </nav>

      {menuOpen && (
        <NavbarMegaMenu
          isDemo={showAppInfo}
          tree={tree}
          loadingMenu={loadingMenu}
          effectiveUser={effectiveUser}
          permissionsLoaded={permissionsLoaded}
          initials={initials}
          brandBg={brandBg}
          onClose={() => setMenuOpen(false)}
          onNavigate={handleNavigation}
          onAdminAccess={handleAdminAccess}
          onLogout={logout}
          goToItem={goToItem}
        />
      )}

      <div className="h-[80px] md:h-[100px]" />
    </>
  );
}
